import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { logger } from 'src/logger';
import { TICKETS_FOR_OE_ERROR_URL } from 'src/constants/AppConstants';
import { useNotification } from './NotificationProvider';
import { FiltersStatus } from './SharedFiltersProvider';
import { FinTechOpsCategories, FinTechOpsMessages } from '../../FinTechOpsConstants';
import { PolicyEngineDashboardItems } from '../../fintech-ops-dashboard/FinTechOpsDataInterfaces';
import { PolicyEngineParsedDetails } from '../../policy-engine/PolicyEngineInterface';
import { usePolicyEngineData } from '../hooks/usePolicyEngineData';

export interface PolicyEngineContextValue {
  finTechOpsPolicyEngineDataStatus: FiltersStatus;
  setFinTechOpsPolicyEngineDataStatus: (s: FiltersStatus) => void;
  finTechOpsPolicyEngineParsedData: PolicyEngineParsedDetails;
  setFinTechOpsPolicyEngineParsedData: (d: PolicyEngineParsedDetails) => void;
  finTechOpsPolicyEngineDashboardData: PolicyEngineDashboardItems;
  setFinTechOpsPolicyEngineDashboardData: (d: PolicyEngineDashboardItems) => void;
}

const EMPTY_PARSED = {} as PolicyEngineParsedDetails;
const EMPTY_DASHBOARD = {} as PolicyEngineDashboardItems;

const defaultValue: PolicyEngineContextValue = {
  finTechOpsPolicyEngineDataStatus: 'loading',
  setFinTechOpsPolicyEngineDataStatus: () => {},
  finTechOpsPolicyEngineParsedData: EMPTY_PARSED,
  setFinTechOpsPolicyEngineParsedData: () => {},
  finTechOpsPolicyEngineDashboardData: EMPTY_DASHBOARD,
  setFinTechOpsPolicyEngineDashboardData: () => {}
};

const PolicyEngineContext = createContext<PolicyEngineContextValue>(defaultValue);

export const usePolicyEngine = () => useContext(PolicyEngineContext);

interface Props {
  children: React.ReactNode;
}

export const PolicyEngineProvider = ({ children }: Props) => {
  const { data, loading, error } = usePolicyEngineData();
  const { displayNotification } = useNotification();
  const [parsed, setParsed] = useState<PolicyEngineParsedDetails>(EMPTY_PARSED);
  const [dashboard, setDashboard] = useState<PolicyEngineDashboardItems>(EMPTY_DASHBOARD);
  const [status, setStatus] = useState<FiltersStatus>('loading');

  useEffect(() => {
    if (loading) {
      setStatus('loading');
      return;
    }
    if (error) {
      logger.error(`Unable to load ${FinTechOpsCategories.policyEngine} data`, error);
      setStatus('error');
      displayNotification('error', FinTechOpsMessages.APIError, TICKETS_FOR_OE_ERROR_URL);
      return;
    }
    if (data) {
      setParsed(data);
      setStatus('finished');
    }
  }, [loading, error, data, displayNotification]);

  const setFinTechOpsPolicyEngineDataStatus = useCallback((s: FiltersStatus) => setStatus(s), []);
  const setFinTechOpsPolicyEngineParsedData = useCallback((d: PolicyEngineParsedDetails) => setParsed(d), []);
  const setFinTechOpsPolicyEngineDashboardData = useCallback((d: PolicyEngineDashboardItems) => setDashboard(d), []);

  const value = useMemo<PolicyEngineContextValue>(
    () => ({
      finTechOpsPolicyEngineDataStatus: status,
      setFinTechOpsPolicyEngineDataStatus,
      finTechOpsPolicyEngineParsedData: parsed,
      setFinTechOpsPolicyEngineParsedData,
      finTechOpsPolicyEngineDashboardData: dashboard,
      setFinTechOpsPolicyEngineDashboardData
    }),
    [status, parsed, dashboard, setFinTechOpsPolicyEngineDataStatus, setFinTechOpsPolicyEngineParsedData, setFinTechOpsPolicyEngineDashboardData]
  );

  return <PolicyEngineContext.Provider value={value}>{children}</PolicyEngineContext.Provider>;
};
